import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass.js';
import { RadialBlurPass } from './RadialBlur';

export const BLOOM_LAYER = 11;
const BLOOM_STRENGTH_EPSILON = 0.0001;
const RADIAL_BLUR_STRENGTH_EPSILON = 0.001;
const FINAL_OUTPUT_DEFINE = 'SFX_FINAL_OUTPUT';
const NOOP_RESTORE = (): void => {};

export interface SfxOptions {
  bloom?: {
    intensity?: number;
    radius?: number;
    threshold?: number;
  };
  selectiveBloom?: {
    enabled?: boolean;
    intensity?: number;
  };
  colorGrade?: {
    enabled?: boolean;
    lift?: { r: number; g: number; b: number };
    gain?: { r: number; g: number; b: number };
    saturation?: number;
    contrast?: number;
    gamma?: number;
  };
  vignette?: {
    strength?: number;
    softness?: number;
    tintHex?: string;
  };
  chromaticAberration?: {
    amount?: number;
    falloff?: number;
    asymmetry?: number;
  };
  colorGradeExclusions?: THREE.Object3D[];
  halfRes?: boolean;
  motion?: {
    samples?: 4 | 8 | 16;
    strength?: number;
    falloff?: number;
    chromaticAmount?: number;
  };
  pixelation?: {
    enabled?: boolean;
    width?: number;
    height?: number;
  };
  heat?: {
    intensity?: number;
  };
  godRays?: {
    /** Master strength multiplier for the radial light shaft accumulator. */
    strength?: number;
    /** Warm sun tint applied to the accumulated samples. */
    tintHex?: string;
    /** Per-step exposure decay along each ray (0..1). 0.96 reads as soft, 0.88 as crisp. */
    decay?: number;
    /** Density of the march — fraction of the ray covered per N samples (typically 0.92–0.98). */
    density?: number;
  };
}

export interface SfxTraceHooks {
  measureCpu?: (name: string, fn: () => void) => void;
  measureGpu?: (name: string, fn: () => void) => void;
}

export interface SfxRuntime {
  getBloomEnabled(): boolean;
  setBloomStyle(opts: NonNullable<SfxOptions['bloom']>): void;
  setSelectiveBloomStyle(opts: NonNullable<SfxOptions['selectiveBloom']>): void;
  setColorGradeStyle(opts: NonNullable<SfxOptions['colorGrade']>): void;
  setColorGradeEnabled(enabled: boolean): void;
  setBloomEnabled(enabled: boolean): void;
  setMotionBlurEnabled(enabled: boolean): void;
  setColorGradeExclusions(exclusions: THREE.Object3D[]): void;
  setVignetteStyle(opts: NonNullable<SfxOptions['vignette']>): void;
  setChromaticAberrationStyle(opts: NonNullable<SfxOptions['chromaticAberration']>): void;
  setSfxStyle(opts: SfxOptions): void;
  addBloomTarget(object: THREE.Object3D): void;
  setRadialBlurStrength(value: number): void;
  setRadialBlurCenter(x: number, y: number): void;
  setRadialBlurSamples(samples: 4 | 8 | 16): void;
  setRadialBlurFalloff(value: number): void;
  setRadialBlurChromatic(value: number): void;
  setPixelation(enabled: boolean, width?: number, height?: number): void;
  setHeatIntensity(value: number): void;
  setHeatPoints(points: ReadonlyArray<{ position: THREE.Vector3; radius: number }>): void;
  /**
   * Update where the god-ray accumulator radiates from in NDC space (-1..+1
   * with 0,0 at screen centre). Drive this from the camera-projected sun
   * direction every frame; pass strength = 0 when the sun is behind the
   * camera so the accumulator skips entirely.
   */
  setGodRaySun(ndcX: number, ndcY: number, strength: number): void;
  setGodRayStyle(opts: NonNullable<SfxOptions['godRays']>): void;
  setSize(width: number, height: number): void;
  setPixelRatio(pixelRatio: number): void;
  setRenderScale(scale: number): void;
  // WebGPU-only live TAAU tuning. WebGL backends return null/no-op.
  setTaauTuning?(partial: { currentFrameWeight?: number; maxVelocityLength?: number; rcasSharpness?: number }): void;
  getTaauTuning?(): { currentFrameWeight: number; maxVelocityLength: number; rcasSharpness: number } | null;
  render(deltaSeconds?: number, trace?: SfxTraceHooks): void;
  dispose(): void;
}

const COLOR_GRADE_SHADER = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    tMask: { value: null as THREE.Texture | null },
    uUseMask: { value: 0 },
    uLift: { value: new THREE.Vector3(0, 0, 0) },
    uGain: { value: new THREE.Vector3(1, 1, 1) },
    uSaturation: { value: 1 },
    uContrast: { value: 1 },
    uGamma: { value: 1 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform sampler2D tMask;
    uniform float uUseMask;
    uniform vec3 uLift;
    uniform vec3 uGain;
    uniform float uSaturation;
    uniform float uContrast;
    uniform float uGamma;
    varying vec2 vUv;

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec3 color = max(texel.rgb, vec3(0.0));
      color = color * uGain + uLift * (vec3(1.0) - clamp(color, 0.0, 1.0));
      color = pow(max(color / 0.18, vec3(0.0)), vec3(max(uContrast, 0.001))) * 0.18;
      float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      color = mix(vec3(luma), color, uSaturation);
      color = pow(max(color, vec3(0.0)), vec3(1.0 / clamp(uGamma, 0.85, 1.15)));
      float keepOriginal = uUseMask * step(0.5, texture2D(tMask, vUv).r);
      color = mix(color, texel.rgb, keepOriginal);
      gl_FragColor = vec4(color, texel.a);
    }
  `,
};

class ColorGradePass extends Pass {
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;
  private readonly material: THREE.ShaderMaterial;
  private readonly fsQuad: FullScreenQuad;
  private readonly maskTarget: THREE.WebGLRenderTarget;
  private readonly maskMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide,
    fog: false,
    toneMapped: false,
  });
  private readonly maskAllowed = new Set<THREE.Object3D>();
  private readonly maskVisibility: Array<{ object: THREE.Object3D; visible: boolean }> = [];
  private readonly prevClearColor = new THREE.Color();
  private exclusions: THREE.Object3D[] = [];

  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    opts: NonNullable<SfxOptions['colorGrade']> = {},
    exclusions: THREE.Object3D[] = [],
  ) {
    super();
    this.scene = scene;
    this.camera = camera;
    this.material = new THREE.ShaderMaterial(COLOR_GRADE_SHADER);
    this.fsQuad = new FullScreenQuad(this.material);
    this.maskTarget = new THREE.WebGLRenderTarget(1, 1, {
      depthBuffer: false,
      stencilBuffer: false,
    });
    this.setExclusions(exclusions);
    this.setStyle(opts);
  }

  setStyle(opts: NonNullable<SfxOptions['colorGrade']>): void {
    this.enabled = opts.enabled ?? true;
    const lift = opts.lift ?? { r: 0, g: 0, b: 0 };
    const gain = opts.gain ?? { r: 1, g: 1, b: 1 };
    ((this.material.uniforms.uLift as THREE.IUniform<THREE.Vector3>).value).set(lift.r, lift.g, lift.b);
    ((this.material.uniforms.uGain as THREE.IUniform<THREE.Vector3>).value).set(gain.r, gain.g, gain.b);
    (this.material.uniforms.uSaturation as THREE.IUniform<number>).value = Math.max(0, opts.saturation ?? 1);
    (this.material.uniforms.uContrast as THREE.IUniform<number>).value = Math.max(0.001, opts.contrast ?? 1);
    (this.material.uniforms.uGamma as THREE.IUniform<number>).value = THREE.MathUtils.clamp(opts.gamma ?? 1, 0.85, 1.15);
  }

  setExclusions(exclusions: THREE.Object3D[]): void {
    this.exclusions = exclusions;
    (this.material.uniforms.uUseMask as THREE.IUniform<number>).value = exclusions.length > 0 ? 1 : 0;
    this.rebuildMaskAllowed();
  }

  override setSize(width: number, height: number): void {
    this.maskTarget.setSize(Math.max(1, width), Math.max(1, height));
  }

  override render(
    renderer: THREE.WebGLRenderer,
    writeBuffer: THREE.WebGLRenderTarget,
    readBuffer: THREE.WebGLRenderTarget,
  ): void {
    if (this.exclusions.length > 0) {
      this.renderMask(renderer);
      (this.material.uniforms.tMask as THREE.IUniform<THREE.Texture>).value = this.maskTarget.texture;
    }
    (this.material.uniforms.tDiffuse as THREE.IUniform<THREE.Texture>).value = readBuffer.texture;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this.fsQuad.render(renderer);
  }

  override dispose(): void {
    this.maskTarget.dispose();
    this.maskMaterial.dispose();
    this.material.dispose();
    this.fsQuad.dispose();
  }

  private renderMask(renderer: THREE.WebGLRenderer): void {
    let visibilityCount = 0;
    this.scene.traverse((object) => {
      const record = this.maskVisibility[visibilityCount] ?? { object, visible: object.visible };
      record.object = object;
      record.visible = object.visible;
      this.maskVisibility[visibilityCount] = record;
      visibilityCount++;
      if (object !== this.scene) object.visible = object.visible && this.maskAllowed.has(object);
    });
    this.maskVisibility.length = visibilityCount;

    const prevOverride = this.scene.overrideMaterial;
    const prevTarget = renderer.getRenderTarget();
    renderer.getClearColor(this.prevClearColor);
    const prevClearAlpha = renderer.getClearAlpha();
    this.scene.overrideMaterial = this.maskMaterial;
    renderer.setRenderTarget(this.maskTarget);
    renderer.setClearColor(0x000000, 0);
    renderer.clear();
    renderer.render(this.scene, this.camera);
    this.scene.overrideMaterial = prevOverride;
    for (let i = 0; i < visibilityCount; i++) {
      const record = this.maskVisibility[i];
      record.object.visible = record.visible;
    }
    renderer.setClearColor(this.prevClearColor, prevClearAlpha);
    renderer.setRenderTarget(prevTarget);
  }

  private rebuildMaskAllowed(): void {
    this.maskAllowed.clear();
    for (const root of this.exclusions) {
      root.traverse((object) => this.maskAllowed.add(object));
    }
  }
}

const FULLSCREEN_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const RAW_FULLSCREEN_VERTEX = /* glsl */ `
  precision highp float;

  uniform mat4 modelViewMatrix;
  uniform mat4 projectionMatrix;

  attribute vec3 position;
  attribute vec2 uv;

  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const VIGNETTE_SHADER = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uStrength: { value: 0 },
    uSoftness: { value: 0.55 },
    uTint: { value: new THREE.Color(0x000000) },
  },
  vertexShader: FULLSCREEN_VERTEX,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uStrength;
    uniform float uSoftness;
    uniform vec3 uTint;
    varying vec2 vUv;

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec2 p = vUv * 2.0 - 1.0;
      float edge = smoothstep(max(0.01, 1.0 - uSoftness), 1.25, length(p));
      vec3 color = mix(texel.rgb, texel.rgb * uTint, edge * uStrength);
      gl_FragColor = vec4(color, texel.a);
    }
  `,
};

class VignettePass extends Pass {
  private readonly material = new THREE.ShaderMaterial(VIGNETTE_SHADER);
  private readonly fsQuad = new FullScreenQuad(this.material);

  constructor(opts: NonNullable<SfxOptions['vignette']> = {}) {
    super();
    this.setStyle(opts);
  }

  setStyle(opts: NonNullable<SfxOptions['vignette']>): void {
    const strength = Math.max(0, opts.strength ?? 0);
    this.enabled = strength > 0.0001;
    (this.material.uniforms.uStrength as THREE.IUniform<number>).value = strength;
    (this.material.uniforms.uSoftness as THREE.IUniform<number>).value = THREE.MathUtils.clamp(opts.softness ?? 0.55, 0.05, 1.5);
    ((this.material.uniforms.uTint as THREE.IUniform<THREE.Color>).value).set(opts.tintHex ?? '#000000');
  }

  override render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget): void {
    (this.material.uniforms.tDiffuse as THREE.IUniform<THREE.Texture>).value = readBuffer.texture;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this.fsQuad.render(renderer);
  }

  override dispose(): void {
    this.material.dispose();
    this.fsQuad.dispose();
  }
}

const CHROMA_SHADER = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uAmount: { value: 0 },
    uFalloff: { value: 1.8 },
    uAsymmetry: { value: 1 },
  },
  vertexShader: FULLSCREEN_VERTEX,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uAmount;
    uniform float uFalloff;
    uniform float uAsymmetry;
    varying vec2 vUv;

    void main() {
      vec2 dir = vUv - vec2(0.5);
      float radius = length(dir);
      vec2 offset = normalize(dir + vec2(0.00001)) * uAmount * pow(radius, max(0.1, uFalloff));
      float r = texture2D(tDiffuse, vUv + offset * max(0.0, uAsymmetry)).r;
      float g = texture2D(tDiffuse, vUv).g;
      float b = texture2D(tDiffuse, vUv - offset).b;
      float a = texture2D(tDiffuse, vUv).a;
      gl_FragColor = vec4(r, g, b, a);
    }
  `,
};

class ChromaticAberrationPass extends Pass {
  private readonly material = new THREE.ShaderMaterial(CHROMA_SHADER);
  private readonly fsQuad = new FullScreenQuad(this.material);

  constructor(opts: NonNullable<SfxOptions['chromaticAberration']> = {}) {
    super();
    this.setStyle(opts);
  }

  setStyle(opts: NonNullable<SfxOptions['chromaticAberration']>): void {
    const amount = Math.max(0, opts.amount ?? 0);
    this.enabled = amount > 0.00001;
    (this.material.uniforms.uAmount as THREE.IUniform<number>).value = amount;
    (this.material.uniforms.uFalloff as THREE.IUniform<number>).value = Math.max(0.1, opts.falloff ?? 1.8);
    (this.material.uniforms.uAsymmetry as THREE.IUniform<number>).value = Math.max(0, opts.asymmetry ?? 1);
  }

  override render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget): void {
    (this.material.uniforms.tDiffuse as THREE.IUniform<THREE.Texture>).value = readBuffer.texture;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this.fsQuad.render(renderer);
  }

  override dispose(): void {
    this.material.dispose();
    this.fsQuad.dispose();
  }
}

const GRADE_CHROMA_VIGNETTE_SHADER = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    toneMappingExposure: { value: 1 },
    uColorGradeEnabled: { value: 1 },
    uLift: { value: new THREE.Vector3(0, 0, 0) },
    uGain: { value: new THREE.Vector3(1, 1, 1) },
    uSaturation: { value: 1 },
    uContrast: { value: 1 },
    uGamma: { value: 1 },
    uChromaAmount: { value: 0 },
    uChromaFalloff: { value: 1.8 },
    uChromaAsymmetry: { value: 1 },
    uVignetteStrength: { value: 0 },
    uVignetteSoftness: { value: 0.55 },
    uVignetteTint: { value: new THREE.Color(0x000000) },
  },
  vertexShader: RAW_FULLSCREEN_VERTEX,
  fragmentShader: /* glsl */ `
    precision highp float;

    uniform sampler2D tDiffuse;
    uniform float uColorGradeEnabled;
    uniform vec3 uLift;
    uniform vec3 uGain;
    uniform float uSaturation;
    uniform float uContrast;
    uniform float uGamma;
    uniform float uChromaAmount;
    uniform float uChromaFalloff;
    uniform float uChromaAsymmetry;
    uniform float uVignetteStrength;
    uniform float uVignetteSoftness;
    uniform vec3 uVignetteTint;
    varying vec2 vUv;

    #ifdef ${FINAL_OUTPUT_DEFINE}
      #include <tonemapping_pars_fragment>
      #include <colorspace_pars_fragment>
    #endif

    vec3 applyGrade(vec3 inputColor) {
      if (uColorGradeEnabled < 0.5) return inputColor;
      vec3 color = max(inputColor, vec3(0.0));
      color = color * uGain + uLift * (vec3(1.0) - clamp(color, 0.0, 1.0));
      color = pow(max(color / 0.18, vec3(0.0)), vec3(max(uContrast, 0.001))) * 0.18;
      float luma = dot(color, vec3(0.2126, 0.7152, 0.0722));
      color = mix(vec3(luma), color, uSaturation);
      return pow(max(color, vec3(0.0)), vec3(1.0 / clamp(uGamma, 0.85, 1.15)));
    }

    vec4 sampleGraded(vec2 uv) {
      vec4 texel = texture2D(tDiffuse, uv);
      return vec4(applyGrade(texel.rgb), texel.a);
    }

    void main() {
      vec4 center = sampleGraded(vUv);
      vec3 color = center.rgb;
      if (uChromaAmount > 0.00001) {
        vec2 dir = vUv - vec2(0.5);
        float radius = length(dir);
        vec2 offset = normalize(dir + vec2(0.00001)) * uChromaAmount * pow(radius, max(0.1, uChromaFalloff));
        float r = sampleGraded(vUv + offset * max(0.0, uChromaAsymmetry)).r;
        float b = sampleGraded(vUv - offset).b;
        color = vec3(r, center.g, b);
      }
      if (uVignetteStrength > 0.0001) {
        vec2 p = vUv * 2.0 - 1.0;
        float edge = smoothstep(max(0.01, 1.0 - uVignetteSoftness), 1.25, length(p));
        color = mix(color, color * uVignetteTint, edge * uVignetteStrength);
      }
      gl_FragColor = vec4(color, center.a);
      #ifdef ${FINAL_OUTPUT_DEFINE}
        #ifdef LINEAR_TONE_MAPPING
          gl_FragColor.rgb = LinearToneMapping(gl_FragColor.rgb);
        #elif defined(REINHARD_TONE_MAPPING)
          gl_FragColor.rgb = ReinhardToneMapping(gl_FragColor.rgb);
        #elif defined(CINEON_TONE_MAPPING)
          gl_FragColor.rgb = OptimizedCineonToneMapping(gl_FragColor.rgb);
        #elif defined(ACES_FILMIC_TONE_MAPPING)
          gl_FragColor.rgb = ACESFilmicToneMapping(gl_FragColor.rgb);
        #elif defined(AGX_TONE_MAPPING)
          gl_FragColor.rgb = AgXToneMapping(gl_FragColor.rgb);
        #elif defined(NEUTRAL_TONE_MAPPING)
          gl_FragColor.rgb = NeutralToneMapping(gl_FragColor.rgb);
        #endif
        #ifdef SRGB_TRANSFER
          gl_FragColor = sRGBTransferOETF(gl_FragColor);
        #endif
      #endif
    }
  `,
};

class GradeChromaVignettePass extends Pass {
  private readonly material = new THREE.RawShaderMaterial(GRADE_CHROMA_VIGNETTE_SHADER);
  private readonly fsQuad = new FullScreenQuad(this.material);
  private readonly finalOutput: boolean;
  private colorGradeEnabled = true;
  private chromaEnabled = false;
  private vignetteEnabled = false;
  private outputColorSpace: string | null = null;
  private toneMapping: THREE.ToneMapping | null = null;

  constructor(opts: {
    colorGrade?: NonNullable<SfxOptions['colorGrade']>;
    chromaticAberration?: NonNullable<SfxOptions['chromaticAberration']>;
    vignette?: NonNullable<SfxOptions['vignette']>;
  } = {}, finalOutput = false) {
    super();
    this.finalOutput = finalOutput;
    this.setColorGradeStyle(opts.colorGrade ?? {});
    this.setChromaticAberrationStyle(opts.chromaticAberration ?? {});
    this.setVignetteStyle(opts.vignette ?? {});
  }

  setColorGradeStyle(opts: NonNullable<SfxOptions['colorGrade']>): void {
    this.colorGradeEnabled = opts.enabled ?? true;
    (this.material.uniforms.uColorGradeEnabled as THREE.IUniform<number>).value = this.colorGradeEnabled ? 1 : 0;
    const lift = opts.lift ?? { r: 0, g: 0, b: 0 };
    const gain = opts.gain ?? { r: 1, g: 1, b: 1 };
    ((this.material.uniforms.uLift as THREE.IUniform<THREE.Vector3>).value).set(lift.r, lift.g, lift.b);
    ((this.material.uniforms.uGain as THREE.IUniform<THREE.Vector3>).value).set(gain.r, gain.g, gain.b);
    (this.material.uniforms.uSaturation as THREE.IUniform<number>).value = Math.max(0, opts.saturation ?? 1);
    (this.material.uniforms.uContrast as THREE.IUniform<number>).value = Math.max(0.001, opts.contrast ?? 1);
    (this.material.uniforms.uGamma as THREE.IUniform<number>).value = THREE.MathUtils.clamp(opts.gamma ?? 1, 0.85, 1.15);
    this.updateEnabled();
  }

  setColorGradeEnabled(enabled: boolean): void {
    this.colorGradeEnabled = enabled;
    (this.material.uniforms.uColorGradeEnabled as THREE.IUniform<number>).value = enabled ? 1 : 0;
    this.updateEnabled();
  }

  setChromaticAberrationStyle(opts: NonNullable<SfxOptions['chromaticAberration']>): void {
    const amount = Math.max(0, opts.amount ?? 0);
    this.chromaEnabled = amount > 0.00001;
    (this.material.uniforms.uChromaAmount as THREE.IUniform<number>).value = amount;
    (this.material.uniforms.uChromaFalloff as THREE.IUniform<number>).value = Math.max(0.1, opts.falloff ?? 1.8);
    (this.material.uniforms.uChromaAsymmetry as THREE.IUniform<number>).value = Math.max(0, opts.asymmetry ?? 1);
    this.updateEnabled();
  }

  setVignetteStyle(opts: NonNullable<SfxOptions['vignette']>): void {
    const strength = Math.max(0, opts.strength ?? 0);
    this.vignetteEnabled = strength > 0.0001;
    (this.material.uniforms.uVignetteStrength as THREE.IUniform<number>).value = strength;
    (this.material.uniforms.uVignetteSoftness as THREE.IUniform<number>).value = THREE.MathUtils.clamp(opts.softness ?? 0.55, 0.05, 1.5);
    ((this.material.uniforms.uVignetteTint as THREE.IUniform<THREE.Color>).value).set(opts.tintHex ?? '#000000');
    this.updateEnabled();
  }

  override render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget): void {
    this.updateOutputDefines(renderer);
    (this.material.uniforms.tDiffuse as THREE.IUniform<THREE.Texture>).value = readBuffer.texture;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this.fsQuad.render(renderer);
  }

  override dispose(): void {
    this.material.dispose();
    this.fsQuad.dispose();
  }

  private updateEnabled(): void {
    this.enabled = this.finalOutput || this.colorGradeEnabled || this.chromaEnabled || this.vignetteEnabled;
  }

  private updateOutputDefines(renderer: THREE.WebGLRenderer): void {
    if (!this.finalOutput) return;
    (this.material.uniforms.toneMappingExposure as THREE.IUniform<number>).value = renderer.toneMappingExposure;
    if (this.outputColorSpace === renderer.outputColorSpace && this.toneMapping === renderer.toneMapping) return;

    this.outputColorSpace = renderer.outputColorSpace;
    this.toneMapping = renderer.toneMapping;
    const defines: Record<string, string> = { [FINAL_OUTPUT_DEFINE]: '' };
    if (THREE.ColorManagement.getTransfer(renderer.outputColorSpace) === THREE.SRGBTransfer) defines.SRGB_TRANSFER = '';
    if (renderer.toneMapping === THREE.LinearToneMapping) defines.LINEAR_TONE_MAPPING = '';
    else if (renderer.toneMapping === THREE.ReinhardToneMapping) defines.REINHARD_TONE_MAPPING = '';
    else if (renderer.toneMapping === THREE.CineonToneMapping) defines.CINEON_TONE_MAPPING = '';
    else if (renderer.toneMapping === THREE.ACESFilmicToneMapping) defines.ACES_FILMIC_TONE_MAPPING = '';
    else if (renderer.toneMapping === THREE.AgXToneMapping) defines.AGX_TONE_MAPPING = '';
    else if (renderer.toneMapping === THREE.NeutralToneMapping) defines.NEUTRAL_TONE_MAPPING = '';
    this.material.defines = defines;
    this.material.needsUpdate = true;
  }
}

const HEAT_POINT_COUNT = 6;

const HEAT_SHADER = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uTime: { value: 0 },
    uIntensity: { value: 0 },
    uViewProj: { value: new THREE.Matrix4() },
    uHeatPoints: { value: new Array(HEAT_POINT_COUNT).fill(null).map(() => new THREE.Vector3()) },
    uHeatRadii: { value: new Float32Array(HEAT_POINT_COUNT) },
    uAspect: { value: 1 },
  },
  vertexShader: FULLSCREEN_VERTEX,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uTime;
    uniform float uIntensity;
    uniform mat4 uViewProj;
    uniform vec3 uHeatPoints[${HEAT_POINT_COUNT}];
    uniform float uHeatRadii[${HEAT_POINT_COUNT}];
    uniform float uAspect;
    varying vec2 vUv;

    float h(vec3 p) {
      p = fract(p * 0.3183099 + vec3(0.1, 0.2, 0.3));
      p *= 17.0;
      return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
    }
    float n3(vec3 p) {
      vec3 i = floor(p);
      vec3 f = fract(p);
      f = f * f * (3.0 - 2.0 * f);
      float n000 = h(i + vec3(0.0, 0.0, 0.0));
      float n100 = h(i + vec3(1.0, 0.0, 0.0));
      float n010 = h(i + vec3(0.0, 1.0, 0.0));
      float n110 = h(i + vec3(1.0, 1.0, 0.0));
      float n001 = h(i + vec3(0.0, 0.0, 1.0));
      float n101 = h(i + vec3(1.0, 0.0, 1.0));
      float n011 = h(i + vec3(0.0, 1.0, 1.0));
      float n111 = h(i + vec3(1.0, 1.0, 1.0));
      float nx00 = mix(n000, n100, f.x);
      float nx10 = mix(n010, n110, f.x);
      float nx01 = mix(n001, n101, f.x);
      float nx11 = mix(n011, n111, f.x);
      return mix(mix(nx00, nx10, f.y), mix(nx01, nx11, f.y), f.z);
    }

    void main() {
      vec2 uv = vUv;
      if (uIntensity >= 0.001) {
        float mask = 0.0;
        for (int i = 0; i < ${HEAT_POINT_COUNT}; i++) {
          float r = uHeatRadii[i];
          if (r <= 0.0001) continue;
          vec4 ndc = uViewProj * vec4(uHeatPoints[i], 1.0);
          if (ndc.w <= 0.0) continue;
          vec2 screen = ndc.xy / ndc.w * 0.5 + 0.5;
          vec2 d = (uv - screen) * vec2(uAspect, 1.0);
          mask = max(mask, 1.0 - smoothstep(0.0, r, length(d)));
        }
        if (mask > 0.0) {
          vec2 nuv = uv * vec2(8.0, 6.0) + vec2(0.0, -uTime * 0.6);
          float a = n3(vec3(nuv, 0.0));
          float b = n3(vec3(nuv * 2.7 + vec2(13.1, -2.7), uTime * 0.45));
          uv += vec2(a - 0.5, b - 0.5) * 0.009 * mask * uIntensity;
        }
      }
      gl_FragColor = texture2D(tDiffuse, uv);
    }
  `,
};

class HeatDistortionPass extends Pass {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly camera: THREE.Camera;
  private readonly material = new THREE.ShaderMaterial(HEAT_SHADER);
  private readonly fsQuad = new FullScreenQuad(this.material);
  private readonly rendererSize = new THREE.Vector2();

  constructor(renderer: THREE.WebGLRenderer, camera: THREE.Camera, opts: NonNullable<SfxOptions['heat']> = {}) {
    super();
    this.renderer = renderer;
    this.camera = camera;
    this.setIntensity(opts.intensity ?? 0);
  }

  setIntensity(value: number): void {
    const clamped = THREE.MathUtils.clamp(value, 0, 2);
    this.enabled = clamped > 0.001;
    (this.material.uniforms.uIntensity as THREE.IUniform<number>).value = clamped;
  }

  setHeatPoints(points: ReadonlyArray<{ position: THREE.Vector3; radius: number }>): void {
    const positions = (this.material.uniforms.uHeatPoints as THREE.IUniform<THREE.Vector3[]>).value;
    const radii = (this.material.uniforms.uHeatRadii as THREE.IUniform<Float32Array>).value;
    for (let i = 0; i < HEAT_POINT_COUNT; i++) {
      const sample = points[i];
      if (sample) {
        positions[i].copy(sample.position);
        radii[i] = Math.max(0, sample.radius);
      } else {
        radii[i] = 0;
      }
    }
  }

  updatePerFrame(deltaSeconds: number): void {
    (this.material.uniforms.uTime as THREE.IUniform<number>).value += deltaSeconds;
    const viewProj = (this.material.uniforms.uViewProj as THREE.IUniform<THREE.Matrix4>).value;
    viewProj.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
    this.renderer.getSize(this.rendererSize);
    (this.material.uniforms.uAspect as THREE.IUniform<number>).value = this.rendererSize.x / Math.max(1, this.rendererSize.y);
  }

  override render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget): void {
    (this.material.uniforms.tDiffuse as THREE.IUniform<THREE.Texture>).value = readBuffer.texture;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this.fsQuad.render(renderer);
  }

  override dispose(): void {
    this.material.dispose();
    this.fsQuad.dispose();
  }
}

const PIXELATION_SHADER = {
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    uPixelSize: { value: new THREE.Vector2(1, 1) },
    uEnabled: { value: 0 },
  },
  vertexShader: FULLSCREEN_VERTEX,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 uPixelSize;
    uniform float uEnabled;
    varying vec2 vUv;

    void main() {
      vec2 uv = vUv;
      if (uEnabled > 0.5) {
        vec2 cells = max(vec2(1.0), 1.0 / uPixelSize);
        uv = (floor(uv * cells) + 0.5) / cells;
      }
      gl_FragColor = texture2D(tDiffuse, uv);
    }
  `,
};

class PixelationPass extends Pass {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly material = new THREE.ShaderMaterial(PIXELATION_SHADER);
  private readonly fsQuad = new FullScreenQuad(this.material);
  private readonly rendererSize = new THREE.Vector2();
  private pixelWidth = 480;
  private pixelHeight = 270;

  constructor(renderer: THREE.WebGLRenderer, opts: NonNullable<SfxOptions['pixelation']> = {}) {
    super();
    this.renderer = renderer;
    this.setPixelation(opts.enabled ?? false, opts.width, opts.height);
  }

  setPixelation(enabled: boolean, width?: number, height?: number): void {
    this.enabled = enabled;
    (this.material.uniforms.uEnabled as THREE.IUniform<number>).value = enabled ? 1 : 0;
    if (typeof width === 'number') this.pixelWidth = Math.max(64, Math.floor(width));
    if (typeof height === 'number') this.pixelHeight = Math.max(36, Math.floor(height));
    this.applyPixelSize();
  }

  override setSize(_width: number, _height: number): void {
    this.applyPixelSize();
  }

  override render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget): void {
    (this.material.uniforms.tDiffuse as THREE.IUniform<THREE.Texture>).value = readBuffer.texture;
    renderer.setRenderTarget(this.renderToScreen ? null : writeBuffer);
    if (this.clear) renderer.clear();
    this.fsQuad.render(renderer);
  }

  override dispose(): void {
    this.material.dispose();
    this.fsQuad.dispose();
  }

  private applyPixelSize(): void {
    this.renderer.getSize(this.rendererSize);
    const pixelSize = (this.material.uniforms.uPixelSize as THREE.IUniform<THREE.Vector2>).value;
    pixelSize.set(this.pixelWidth / Math.max(1, this.rendererSize.x), this.pixelHeight / Math.max(1, this.rendererSize.y));
  }
}

/**
 * Unified full-frame SFX pipeline for map rendering and RigReview.
 *
 * Three.js expands `#include <...>` shader chunks after `onBeforeCompile`, but
 * this pipeline uses direct ShaderMaterial passes, so every panel control maps
 * to a concrete uniform here.
 */
export class SfxPipeline implements SfxRuntime {
  readonly composer: EffectComposer;
  private readonly renderer: THREE.WebGLRenderer;
  private bloomPass: UnrealBloomPass;
  private bloomDebugEnabled = true;
  private readonly selectiveBloomTargets = new Set<THREE.Object3D>();
  private selectiveBloomEnabled = false;
  private selectiveBloomIntensity = 1;
  private colorGradePass: ColorGradePass | null = null;
  private chromaticAberrationPass: ChromaticAberrationPass | null = null;
  private vignettePass: VignettePass | null = null;
  private combinedGradePass: GradeChromaVignettePass | null = null;
  private halfRes: boolean;
  private radialBlur: RadialBlurPass | null = null;
  private radialBlurDebugEnabled = true;
  private radialBlurStrength = 0;
  private heatPass: HeatDistortionPass | null = null;
  private pixelationPass: PixelationPass | null = null;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    opts: SfxOptions = {},
  ) {
    this.renderer = renderer;
    this.halfRes = opts.halfRes ?? true;
    this.composer = new EffectComposer(renderer);

    this.composer.addPass(new RenderPass(scene, camera));

    const size = new THREE.Vector2();
    renderer.getSize(size);
    if (this.halfRes) size.multiplyScalar(0.5);

    this.bloomPass = new UnrealBloomPass(
      size,
      opts.bloom?.intensity ?? 0.42,
      opts.bloom?.radius ?? 0.4,
      opts.bloom?.threshold ?? 1.6,
    );
    this.composer.addPass(this.bloomPass);
    this.setSelectiveBloomStyle(opts.selectiveBloom ?? {});
    this.updateBloomPassEnabled();

    if (opts.motion) {
      this.radialBlurStrength = Math.max(0, opts.motion.strength ?? 0);
      this.radialBlur = new RadialBlurPass({
        samples: opts.motion.samples ?? 8,
        strength: this.radialBlurStrength,
        falloff: opts.motion.falloff,
        chromaticAmount: opts.motion.chromaticAmount,
      });
      this.updateRadialBlurPassEnabled();
      this.composer.addPass(this.radialBlur);
    }

    if (opts.heat) {
      this.heatPass = new HeatDistortionPass(renderer, camera, opts.heat);
      this.composer.addPass(this.heatPass);
    }

    const colorGradeExclusions = opts.colorGradeExclusions ?? [];
    const fuseFinalOutput = colorGradeExclusions.length === 0 && !opts.pixelation;
    if (colorGradeExclusions.length === 0) {
      this.combinedGradePass = new GradeChromaVignettePass({
        colorGrade: opts.colorGrade,
        chromaticAberration: opts.chromaticAberration,
        vignette: opts.vignette,
      }, fuseFinalOutput);
      this.composer.addPass(this.combinedGradePass);
    } else {
      this.colorGradePass = new ColorGradePass(scene, camera, opts.colorGrade, colorGradeExclusions);
      this.composer.addPass(this.colorGradePass);
      this.chromaticAberrationPass = new ChromaticAberrationPass(opts.chromaticAberration);
      this.composer.addPass(this.chromaticAberrationPass);
      this.vignettePass = new VignettePass(opts.vignette);
      this.composer.addPass(this.vignettePass);
    }

    if (opts.pixelation) {
      this.pixelationPass = new PixelationPass(renderer, opts.pixelation);
      this.composer.addPass(this.pixelationPass);
    }

    if (!fuseFinalOutput) this.composer.addPass(new OutputPass());
  }

  setBloomStyle(opts: NonNullable<SfxOptions['bloom']>): void {
    if (typeof opts.intensity === 'number') this.bloomPass.strength = opts.intensity;
    if (typeof opts.radius === 'number') this.bloomPass.radius = opts.radius;
    if (typeof opts.threshold === 'number') this.bloomPass.threshold = opts.threshold;
    this.updateBloomPassEnabled();
  }

  getBloomEnabled(): boolean {
    return this.bloomDebugEnabled;
  }

  setSelectiveBloomStyle(opts: NonNullable<SfxOptions['selectiveBloom']>): void {
    this.selectiveBloomEnabled = opts.enabled ?? false;
    this.selectiveBloomIntensity = Math.max(0, opts.intensity ?? 1);
  }

  setColorGradeStyle(opts: NonNullable<SfxOptions['colorGrade']>): void {
    this.combinedGradePass?.setColorGradeStyle(opts);
    this.colorGradePass?.setStyle(opts);
  }

  setColorGradeEnabled(enabled: boolean): void {
    this.combinedGradePass?.setColorGradeEnabled(enabled);
    if (this.colorGradePass) this.colorGradePass.enabled = enabled;
  }

  setBloomEnabled(enabled: boolean): void {
    this.bloomDebugEnabled = enabled;
    this.updateBloomPassEnabled();
  }

  setMotionBlurEnabled(enabled: boolean): void {
    this.radialBlurDebugEnabled = enabled;
    this.updateRadialBlurPassEnabled();
  }

  setColorGradeExclusions(exclusions: THREE.Object3D[]): void {
    if (this.combinedGradePass && exclusions.length > 0) {
      console.warn('SfxPipeline: color grade exclusions require split post-processing passes at construction time.');
      return;
    }
    this.colorGradePass?.setExclusions(exclusions);
  }

  setVignetteStyle(opts: NonNullable<SfxOptions['vignette']>): void {
    this.combinedGradePass?.setVignetteStyle(opts);
    this.vignettePass?.setStyle(opts);
  }

  setChromaticAberrationStyle(opts: NonNullable<SfxOptions['chromaticAberration']>): void {
    this.combinedGradePass?.setChromaticAberrationStyle(opts);
    this.chromaticAberrationPass?.setStyle(opts);
  }

  setSfxStyle(opts: SfxOptions): void {
    if (opts.bloom) this.setBloomStyle(opts.bloom);
    if (opts.selectiveBloom) this.setSelectiveBloomStyle(opts.selectiveBloom);
    if (opts.colorGrade) this.setColorGradeStyle(opts.colorGrade);
    if (opts.vignette) this.setVignetteStyle(opts.vignette);
    if (opts.chromaticAberration) this.setChromaticAberrationStyle(opts.chromaticAberration);
    if (opts.motion) {
      if (opts.motion.samples) this.setRadialBlurSamples(opts.motion.samples);
      if (typeof opts.motion.falloff === 'number') this.setRadialBlurFalloff(opts.motion.falloff);
      if (typeof opts.motion.chromaticAmount === 'number') this.setRadialBlurChromatic(opts.motion.chromaticAmount);
    }
  }

  addBloomTarget(object: THREE.Object3D): void {
    this.selectiveBloomTargets.add(object);
  }

  setRadialBlurStrength(value: number): void {
    this.radialBlurStrength = Math.max(0, value);
    this.radialBlur?.setStrength(this.radialBlurStrength);
    this.updateRadialBlurPassEnabled();
  }

  setRadialBlurCenter(x: number, y: number): void {
    this.radialBlur?.setCenter(x, y);
  }

  setRadialBlurSamples(samples: 4 | 8 | 16): void {
    this.radialBlur?.setSamples(samples);
    this.updateRadialBlurPassEnabled();
  }

  setRadialBlurFalloff(value: number): void {
    this.radialBlur?.setFalloff(value);
  }

  setRadialBlurChromatic(value: number): void {
    this.radialBlur?.setChromatic(value);
  }

  setPixelation(enabled: boolean, width?: number, height?: number): void {
    this.pixelationPass?.setPixelation(enabled, width, height);
  }

  setHeatIntensity(value: number): void {
    this.heatPass?.setIntensity(value);
  }

  setHeatPoints(points: ReadonlyArray<{ position: THREE.Vector3; radius: number }>): void {
    this.heatPass?.setHeatPoints(points);
  }

  // God rays are a WebGPU-only enhancement — the WebGL pipeline relies on
  // UnrealBloomPass blooming the sun disc, which produces an acceptable
  // (different but equivalent) sunset shaft look. The interface is shared
  // so main.ts can call these unconditionally; they're no-ops here.
  setGodRaySun(_ndcX: number, _ndcY: number, _strength: number): void {}
  setGodRayStyle(_opts: NonNullable<SfxOptions['godRays']>): void {}

  setSize(width: number, height: number): void {
    this.composer.setSize(width, height);
    if (this.halfRes) {
      this.bloomPass.setSize(Math.max(1, Math.floor(width / 2)), Math.max(1, Math.floor(height / 2)));
    } else {
      this.bloomPass.setSize(width, height);
    }
  }

  setPixelRatio(pixelRatio: number): void {
    this.composer.setPixelRatio(pixelRatio);
  }

  setRenderScale(_scale: number): void {
    // WebGL DRS is driven through EffectComposer pixel ratio changes.
  }

  private updateBloomPassEnabled(): void {
    this.bloomPass.enabled = this.bloomDebugEnabled && this.bloomPass.strength > BLOOM_STRENGTH_EPSILON;
  }

  private updateRadialBlurPassEnabled(): void {
    if (!this.radialBlur) return;
    this.radialBlur.enabled = this.radialBlurDebugEnabled && this.radialBlurStrength >= RADIAL_BLUR_STRENGTH_EPSILON;
  }

  render(deltaSeconds = 1 / 60, trace?: SfxTraceHooks): void {
    this.heatPass?.updatePerFrame(deltaSeconds);
    const restore = this.applySelectiveBloomBoost();
    try {
      if (trace) this.renderTraced(deltaSeconds, trace);
      else this.composer.render(deltaSeconds);
    } finally {
      restore();
    }
  }

  dispose(): void {
    this.composer.dispose();
  }

  private applySelectiveBloomBoost(): () => void {
    if (!this.selectiveBloomEnabled || this.selectiveBloomIntensity <= 0 || this.selectiveBloomTargets.size === 0) {
      return NOOP_RESTORE;
    }

    const changed: Array<[THREE.Material & { emissiveIntensity?: number }, number]> = [];
    for (const root of this.selectiveBloomTargets) {
      root.traverse((object) => {
        if (!(object instanceof THREE.Mesh || object instanceof THREE.Points)) return;
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials) {
          const candidate = material as THREE.Material & { emissiveIntensity?: number };
          if (typeof candidate.emissiveIntensity !== 'number') continue;
          changed.push([candidate, candidate.emissiveIntensity]);
          candidate.emissiveIntensity *= 1 + this.selectiveBloomIntensity;
        }
      });
    }

    return () => {
      for (const [material, value] of changed) material.emissiveIntensity = value;
      this.renderer.setRenderTarget(null);
    };
  }

  private renderTraced(deltaSeconds: number, trace: SfxTraceHooks): void {
    const restored: Array<[Pass, Pass['render']]> = [];
    const passNames = new Map<string, number>();

    for (const pass of this.composer.passes) {
      const original = pass.render;
      const baseName = pass.constructor.name || 'Pass';
      const nextIndex = passNames.get(baseName) ?? 0;
      passNames.set(baseName, nextIndex + 1);
      const name = nextIndex === 0 ? baseName : `${baseName}.${nextIndex + 1}`;
      pass.render = ((renderer, writeBuffer, readBuffer, deltaTime, maskActive) => {
        const run = () => original.call(pass, renderer, writeBuffer, readBuffer, deltaTime, maskActive);
        const runGpu = () => {
          if (trace.measureGpu) trace.measureGpu(`gpu.postfx.${name}`, run);
          else run();
        };
        if (trace.measureCpu) trace.measureCpu(`postfx.pass.${name}`, runGpu);
        else runGpu();
      }) as Pass['render'];
      restored.push([pass, original]);
    }

    try {
      this.composer.render(deltaSeconds);
    } finally {
      for (const [pass, original] of restored) pass.render = original;
    }
  }
}

export function markForBloom(object: THREE.Object3D, pipeline?: SfxRuntime): void {
  object.traverse((child) => {
    child.layers.enable(BLOOM_LAYER);
  });
  pipeline?.addBloomTarget(object);
}
